<template>
    <FetchTemplatesByPage
        v-slot="{
            results: templates,
            isLoading,
            isFullyLoaded,
            fetchPage,
            reload,
        }"
        :query="query"
        :page-size="25"
        @error="handleLoadError"
    >
        <div>
            <TemplateSelectionPanelActions
                v-slot="{
                    handleFromScratchTemplateSelected,
                    handleUnlayerBroadcastTemplateSelected,
                    creatingTemplateId,
                    deletingTemplateId,
                    handleTemplatePreview,
                    closeTemplatePreview,
                    previewTemplate,
                    previewTemplateHtml,
                    isLoadingPreviewSelection,
                    isLoadingPreview,
                    isCreating,
                    isDeleting
                }"
                :source="$options.SOURCES.BROADCAST"
                @template-selected="$emit('template-selected', $event)"
            >
                <div class="cards-layout-container">
                    <TemplateGalleryCardsLayout
                        :error="error"
                        :is-fully-loaded="isFullyLoaded"
                        :is-loading="isLoading"
                        :items="filteredTemplates(templates)"
                        :page-size="25"
                        @fetch="fetchPage"
                    >
                        <template #empty>
                            <EmptyState
                                class="drafts-panel-empty-state"
                                :title="$t('email.broadcast.unlayer.template.selection.drafts.emptyState.title')"
                                :description="$t('Get started with a new broadcast and see drafts you save here')"
                                image="drafts"
                            >
                                <DsFilledButton
                                    :loading="isCreating"
                                    @click="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                                >
                                    {{ $t('email.broadcast.unlayer.template.selection.common.startFromScratch') }}
                                </DsFilledButton>

                                <DsTextButton
                                    class="empty-state-back-button"
                                    @click="goToGallery"
                                >
                                    {{ $t('email.broadcast.unlayer.template.selection.common.goBackToGallery') }}
                                </DsTextButton>
                            </EmptyState>
                        </template>

                        <template #static>
                            <StartFromScratchCard
                                :loading="creatingTemplateId === 'scratch'"
                                :disabled="isCreating"
                                @select="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                            />
                        </template>

                        <template #card="{ item: template }">
                            <TemplateGalleryUnlayerCard
                                :key="template.id"
                                :template="template"
                                :selected="template.id === creatingTemplateId || template.id === deletingTemplateId"
                                :disabled="isCreating || isDeleting"
                                :preview-enabled="previewEnabled"
                                :is-loading-preview="isLoadingPreview"
                                @click="$emit('attempt-to-select-template', { callback: handleUnlayerBroadcastTemplateSelected, type: 'draftOrSent', template })"
                                @preview="handleTemplatePreview(template)"
                            >
                                <template #menu>
                                    <GalleryCardMenu
                                        @delete="confirmDelete(template, reload)"
                                        @edit="$emit('attempt-to-select-template', { callback: handleUnlayerBroadcastTemplateSelected, type: 'draftOrSent', template })"
                                    />
                                </template>
                            </TemplateGalleryUnlayerCard>
                        </template>
                    </TemplateGalleryCardsLayout>

                    <ContentPreviewModal
                        v-if="previewEnabled"
                        data-qa="campaign-email-template-preview-modal"
                        :preview-html="previewTemplateHtml"
                        @close="closeTemplatePreview"
                    >
                        <DsFilledButton
                            data-qa="use-this-template"
                            :loading="isLoadingPreviewSelection"
                            @click="$emit('attempt-to-select-template', { callback: handleUnlayerBroadcastTemplateSelected, type: 'draftOrSent', template: previewTemplate })"
                        >
                            {{ $t('email.broadcast.unlayer.template.selection.common.useThisTemplate') }}
                        </DsFilledButton>
                    </ContentPreviewModal>
                </div>
            </TemplateSelectionPanelActions>
        </div>
    </FetchTemplatesByPage>
</template>

<script>
import FetchTemplatesByPage from './FetchTemplatesByPage';
import TemplateSelectionPanelActions from './TemplateSelectionPanelActions';
import TemplateGalleryCardsLayout from './TemplateGalleryCardsLayout';
import TemplateGalleryUnlayerCard from './TemplateGalleryUnlayerCard';
import ContentPreviewModal from './ContentPreviewModal';
import StartFromScratchCard from './StartFromScratchCard';
import EmptyState from '@/components/shared/EmptyState';
import GalleryCardMenu from './GalleryCardMenu';
import { UNLAYER_BROADCAST_BASE_URL, deleteUnlayerDraft } from '@/api/broadcasts/emailBroadcast';
import {
    BROADCAST_FILTER_VALUES,
    EMAIL_EDITORS,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import { getQueryString } from '@/api/shared';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import { stripHtml } from '@/utils/email.util';

export default {
    name: 'TemplateSelectionDraftsPanel',

    SOURCES,

    components: {
        FetchTemplatesByPage,
        TemplateSelectionPanelActions,
        TemplateGalleryCardsLayout,
        TemplateGalleryUnlayerCard,
        ContentPreviewModal,
        StartFromScratchCard,
        EmptyState,
        GalleryCardMenu,
    },

    props: {
        previewEnabled: Boolean,
        currentTemplateId: String,
    },

    data() {
        return {
            error: null,
        };
    },

    computed: {
        query() {
            const queryString = getQueryString({
                editor: EMAIL_EDITORS.UNLAYER,
                status: BROADCAST_FILTER_VALUES.DRAFT,
            });

            const url = `${UNLAYER_BROADCAST_BASE_URL}/${queryString}`;

            return {
                url,
                method: 'get',
            };
        },
    },

    methods: {
        filteredTemplates(templates) {
            return templates.filter((template) => !!stripHtml(template.html) && template.id !== this.currentTemplateId);
        },

        goToGallery() {
            this.$emit('goToGallery');
        },

        async confirmDelete(template, reload) {
            try {
                await this.$confirm({
                    destructive: true,
                    title: this.$t('global.reallyDelete'),
                    message: this.$t('email.broadcast.unlayer.template.questions.reallyDeleteDraft'),
                    confirmLabel: 'Ok',
                    cancelLabel: 'Cancel',
                });

                this.isLoading = true;

                deleteUnlayerDraft(template.broadcastId)
                    .then(() => this.handleItemDeleted(reload))
                    .catch(this.handleItemDeleteError)
                    .finally(() => {
                        this.isLoading = false;
                    });
            } catch (error) {
                // do nothing
            }
        },

        handleItemDeleted(reload) {
            this.$toast({ message: this.$t('email.broadcast.unlayer.template.success.templateDeleted') });
            reload();
        },

        handleItemDeleteError(reload) {
            this.$error({
                bottom: true,
                message: this.$t('email.broadcast.unlayer.template.error.deleteTemplateFailed'),
                showIcon: true,
            });
            reload();
        },

        handleLoadError(error) {
            this.error = error;
            this.$error({
                bottom: true,
                closeLabel: this.$t('global.okgotit'),
                message: this.$t('email.broadcast.unlayer.template.error.loadingTemplates'),
                showIcon: true,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.cards-layout-container {
    width: 100%;
}

.drafts-panel-empty-state {
    padding-top: $spacing-400;
}

.empty-state-back-button {
    @include margin-start($spacing-100);
}
</style>
